import {apiFetch} from "../client";

// ─── Notifications domain (admin) ─────────────────────────────
// Источник правды по телам: internal/notification/handler.go.
// In-app alerts include merchant balance incidents and global TRC20 watcher
// health/quota incidents. The latter intentionally have merchantId=null.

export type NotificationType =
    | "merchant_low_balance"
    | "merchant_overdraft_exceeded"
    | "chain_watcher_stale"
    | "chain_provider_quota_near_limit"
    | "chain_reconciliation_recovered"
    | "chain_reconciliation_mismatch"
    | "extsource_automation_blocked";
export type NotificationSeverity = "warning" | "critical";
export type NotificationStatus = "active" | "resolved";

export interface NotificationItem {
    id: string;
    /** Строка, а не строгий union — бэкенд может добавить типы без ломки UI. */
    type: NotificationType | string;
    severity: NotificationSeverity | string;
    merchantId: string | null;
    merchantName: string | null;
    orderId: string | null;
    dedupKey: string;
    title: string;
    body: string;
    payload: Record<string, unknown> | null;
    status: NotificationStatus | string;
    createdAt: string;
    resolvedAt: string | null;
    readAt: string | null;
    acknowledgedAt: string | null;
    acknowledgedBy: string | null;
    resolvedBy: string | null;
    resolutionNote: string | null;
    deliveryId: string | null;
    recipientId: string | null;
    recipientName: string | null;
    deliveryState: string | null;
    deliveryAttempts: number | null;
    deliveredAt: string | null;
    deliveryError: string | null;
    deliveryRouteGeneration: number | null;
    telegramMessageId: number | null;
    acknowledgedDeliveryId: string | null;
    acknowledgedRecipientId: string | null;
    acknowledgedRouteGeneration: number | null;
    acknowledgementNote: string | null;
}

export interface NotificationsListResponse {
    items: NotificationItem[];
    limit: number;
    offset: number;
    hasMore: boolean;
    nextOffset?: number | null;
}

/** GET /v1/notifications/summary → {active, unread}. */
export interface NotificationsSummary {
    active: number;
    unread: number;
}

export interface ListNotificationsParams {
    status?: NotificationStatus;
    merchantId?: string;
    limit?: number;
    offset?: number;
}

/** GET /v1/notifications — admin-only список алертов с фильтрами. */
export function listNotifications(
    token: string | null,
    query: ListNotificationsParams = {},
): Promise<NotificationsListResponse> {
    const params = new URLSearchParams();
    if (query.status) params.set("status", query.status);
    if (query.merchantId) params.set("merchantId", query.merchantId);
    if (query.limit != null) params.set("limit", String(query.limit));
    if (query.offset != null) params.set("offset", String(query.offset));
    const suffix = params.toString();
    return apiFetch<NotificationsListResponse>(`/notifications${suffix ? `?${suffix}` : ""}`, token);
}

/** GET /v1/notifications/summary — счётчики для бейджа в сайдбаре. */
export function getNotificationsSummary(token: string | null): Promise<NotificationsSummary> {
    return apiFetch<NotificationsSummary>("/notifications/summary", token);
}

/**
 * POST /v1/notifications/{id}/read — пометить прочитанным (пустой 200).
 *
 * Единственное действие админки над уведомлением. Ручной разбор инцидента
 * контура (`/ack`, `/resolve`, `/close-undelivered`) из интерфейса убран
 * 15.08.2026: инцидент гаснет сам, когда его заявка закрыта. На бэкенде эти
 * ручки остались аварийным выходом и вызываются запросом напрямую.
 */
export function markNotificationRead(token: string | null, id: string): Promise<void> {
    return apiFetch<void>(`/notifications/${encodeURIComponent(id)}/read`, token, {
        method: "POST",
    });
}
